'use client';

import { TJobOffer } from '@eliadmin/core/src/components/homepage/job-offers';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const applySchema = () =>
  z.object({
    name: z.string().min(1),
    surname: z.string().min(1),
    phone: z.string().min(1),
    email: z.string().email(),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true),
    'g-recaptcha-response': z.string().default(''),
  });

export type ApplyFormValues = z.infer<ReturnType<typeof applySchema>>;

export const OfferApplyForm = ({ offer }: { offer: TJobOffer }) => {
  const { t } = useTranslations();
  const form = useForm<ApplyFormValues>({
    resolver: zodResolver(applySchema('')),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button>{t('text813')}</Button>
      </DialogTrigger>
      <DialogContent className={cn('', 'ea-offer-apply__content')}>
        <DialogTitle className={cn('', 'ea-offer-apply__title')}>{offer?.name}</DialogTitle>
        <Separator className={cn('', 'ea-offer-apply__separator')} />
        <Form {...form}>
          <form className={cn('', 'ea-offer-apply__form')}>
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-12 sm:col-span-6', 'ea-apply-form__field')}
                >
                  <FormControl>
                    <Input
                      {...(field as any)}
                      placeholder={t('text33')}
                      className={cn('', 'ea-apply-form__input')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'surname'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-12 sm:col-span-6', 'ea-apply-form__field')}
                >
                  <FormControl>
                    <Input
                      {...(field as any)}
                      placeholder={t('text34')}
                      className={cn('', 'ea-apply-form__input')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'email'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-12 sm:col-span-6', 'ea-apply-form__field')}
                >
                  <FormControl>
                    <Input
                      {...(field as any)}
                      placeholder={t('text35')}
                      className={cn('', 'ea-apply-form__input')}
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'phone'}
              render={({ field }) => (
                <FormItem
                  required
                  className={cn('col-span-12 sm:col-span-6', 'ea-apply-form__field')}
                >
                  <FormControl>
                    <Input
                      {...(field as any)}
                      placeholder={t('text36')}
                      className={cn('', 'ea-apply-form__input')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'text'}
              render={({ field }) => (
                <FormItem required className={cn('col-span-12', 'ea-apply-form__field')}>
                  <FormControl>
                    <Textarea
                      placeholder={t('text57')}
                      {...(field as any)}
                      rows={5}
                      className="rounded-[6px]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className={cn('', 'ea-apply-form__upload-box')}>{t('text814')}</div>
            <div className={cn('', 'ea-apply-form__submit-box')}>
              <Button
                type="submit"
                className={cn('', 'ea-apply-form__submit')}
                isLoading={form.formState.isSubmitting}
              >
                {t('text815')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
